// Constants
import { ClientFlavor, ClientType, MsaTenantId } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { HttpStatus, HttpStatusText } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { PlannerV2AsyncStoragePrefix, PlannerV2AsyncStorageSchemaVersionV1 } from "@ms/uno-constants/lib/local/PersistenceConstants";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { SettingsObjectName } from "@ms/uno-constants/lib/local/configuration/ISettings";
// Flux
import { AsyncDispatcher } from "@ms/uno-fluxcore/lib/local/dispatcher";
// Host Adaptor
import { FailedReason } from "@ms/uno-hostadaptors/lib/local/IHostAdaptor";
import { PersistedObjectStoreSchema, } from "@ms/planner-models/lib/local/client/persistence/PersistedAsyncStorageSchema";
import { AssetLoadingError } from "@ms/uno-errors/lib/local/errors/AssetLoadingError";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Services
import { AuthenticatedAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/AuthenticatedAjaxClient";
import { BasicAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/BasicAjaxClient";
import { GatedGetAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/GatedGetAjaxClient";
import { RequestPrioritizer } from "@ms/uno-services/lib/local/priorityRequestQueue/RequestPrioritizer";
import { generateAjaxClientError } from "@ms/uno-services/lib/local/utilities/ServiceUtilities";
import { Log } from "@ms/uno-telemetry/lib/local/events/Action.event";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { setIsOnlineStatusForLogs } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
import { isMetaOSHub } from "@ms/uno-utilities/lib/local/AppUtilities";
import { generateGuid } from "@ms/uno-utilities/lib/local/Guid";
import { HtmlSettingsReader } from "@ms/uno-utilities/lib/local/HtmlSettingsReader";
import { IndexedDBStorage } from "@ms/uno-utilities/lib/local/storage/IndexedDBStorage";
import { GenericDataPreFetcher } from "../base/preFetcher/GenericDataPreFetcher";
import { Worker } from "@ms/planner-workers/lib/local/workers/Worker";
/**
 * Sets up the AppContext
 * @throws {Error} If there is an error initializing the AppContext
 */
export function setupAppContext() {
    const reader = new HtmlSettingsReader(SettingsObjectName);
    /**
     * Generates a new **client session ID** for initializing the app context.
     *
     * The server-provided session ID from MTRS may remain unchanged in the
     * following scenarios, so we must create a fresh client-side session ID:
     *
     * 1. **First load after Service Worker installation**
     *    All new Service Worker installs cause an initial load where the
     *    server session ID does not rotate.
     *
     * 2. **Fallback load when initial HTML fetch fails**
     *    If the frontend HTML cannot be fetched (e.g., server is down), the app
     *    may still start using cached assets, but the server session ID will not
     *    update.
     *
     * In both cases, generating a new client session ID ensures each logical
     * client session remains uniquely identifiable.
     */
    const clientSessionId = generateGuid();
    AppContext.initializeFromSettings(AppContext.BuildContextSettingsFromSettingsGetter(reader.getSetting.bind(reader)), clientSessionId);
}
/**
 * Gets the context from hostAdaptor
 * @param hostAdaptor The host adaptor
 */
export async function getContext(hostAdaptor) {
    let context;
    try {
        context = await hostAdaptor.getContext();
    }
    catch (e) {
        const err = new AssetLoadingError(e.name, e.message);
        hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message: e.message });
        // TODO - 7545465, 8108486 need to implement errors and error logging
        // Task 8108486: Uno LazyLoading and asset loading error management
        throw err;
    }
    return context;
}
/**
 * Gets the TelemetryContext from the Context
 */
export function getTelemetryContext(context) {
    return {
        locale: context.locale,
        userId: context.userId,
        tenantId: context.tenantId,
        ringId: context.ringId, // Teams ring according to Teams client SDK
        isMSA: context.tenantId === MsaTenantId,
        isGuestUser: context.isGuestUser,
        platform: context.platform,
    };
}
/**
 * Gets the AsyncDispatcher
 * @param configProvider The config provider
 * @param registry flux registry
 * @param storeLoader store loader
 * @param loggers used for logging
 */
export function getAsyncDispatcher(configProvider, registry, storeLoader, loggers) {
    return new AsyncDispatcher(registry, (storeType) => () => storeLoader.loadForDispatch(storeType), (storeType) => () => storeLoader.loadForSubscription(storeType), (event) => Log(event, loggers.logHandler), (tag, traceLevel, message) => loggers.traceLogger.logTrace(tag, traceLevel, message), configProvider().flights.EnableAsyncDispatchV2);
}
/**
 * Gets the RequestPrioritizer
 * @param storeLoader Store loader
 * @param loggers Loggers used for telemetry
 */
export function getRequestPrioritizer(storeLoader, loggers) {
    return new RequestPrioritizer((request) => {
        // Cancel request if it is not for the current view
        const currentViewId = storeLoader.load(Store.Route).getCurrentViewId();
        return currentViewId != null && request.viewIds != null && request.viewIds.indexOf(currentViewId) === -1;
    }, undefined /* customConnectionPriority */, loggers?.traceLogger);
}
/**
 * Method to get persistent async storage instance
 * @param configProvider config provider
 * @param userId user id
 * @param loggers used for logging
 * @returns persisted async storage instance or null if data persistence is disabled or IndexedDB is not supported
 */
export function getPersistentAsyncStorage(configProvider, userId, loggers) {
    let persistentAsyncStorage = null;
    if (configProvider().flights.EnablePersistenceEvo && userId) {
        if (IndexedDBStorage.isIndexedDBSupported()) {
            try {
                persistentAsyncStorage = new IndexedDBStorage(userId, PlannerV2AsyncStoragePrefix + "_" + configProvider().settings.evoPersistentAsyncStorageVersion, PlannerV2AsyncStorageSchemaVersionV1, PersistedObjectStoreSchema, loggers.traceLogger, loggers.performanceMarker);
            }
            catch (error) {
                loggers.traceLogger.logTrace(0x1e2417a2 /* tag_4jb48 */, TraceLevel.Error, error);
            }
        }
        else {
            loggers.traceLogger.logTrace(0x1e2417a1 /* tag_4jb47 */, TraceLevel.Info, "IndexedDB is not supported");
        }
    }
    return persistentAsyncStorage;
}
/**
 * Gets the AuthService
 */
export function getAuthServiceProvider(factory, hostAdaptor, loggers, configProvider, innerAjaxClient = new BasicAjaxClient(configProvider().settings.retrySettings.globalAjaxTimeout, loggers)) {
    const { auth } = configProvider().settings.serviceConfigurations;
    const apiClient = new AuthenticatedAjaxClient(innerAjaxClient, auth.authResourceURI, loggers, hostAdaptor.getAccessToken);
    const gatedApiClient = new GatedGetAjaxClient(apiClient, configProvider().settings.requestDedupSettings.getWindowMs, loggers);
    return factory.getAuthServiceProvider(gatedApiClient, hostAdaptor, configProvider);
}
/**
 * Gets the ServiceProviders
 */
export function getServiceProviders(serviceOrchestrator, authService) {
    return {
        adaptiveCard: serviceOrchestrator.getAdaptiveCardServiceProvider(),
        augloop: serviceOrchestrator.getAugloopServiceProvider(),
        auth: () => authService,
        centro: serviceOrchestrator.getCentroServiceProvider(),
        chatSvcAgg: serviceOrchestrator.getChatSvcAggServiceProvider(),
        copilot: serviceOrchestrator.getCopilotServiceProvider(),
        conversations: serviceOrchestrator.getConversationsServiceProvider(),
        driveItem: serviceOrchestrator.getDriveItemServiceProvider(),
        events: serviceOrchestrator.getEventsServiceProvider(),
        flow: serviceOrchestrator.getFlowServiceProvider(),
        gateway: serviceOrchestrator.getGatewayServiceProvider(),
        iris: serviceOrchestrator.getIrisServiceProvider(),
        groups: serviceOrchestrator.getGroupsServiceProvider(),
        legacyChatSvcAgg: serviceOrchestrator.getLegacyChatSvcAggServiceProvider(),
        loop: serviceOrchestrator.getLoopServiceProvider(),
        loopMts: serviceOrchestrator.getLoopMtsServiceProvider(),
        mars: serviceOrchestrator.getMarsServiceProvider(),
        mru: serviceOrchestrator.getMruServiceProvider(),
        ocps: serviceOrchestrator.getOcpsServiceProvider(),
        oneshell: serviceOrchestrator.getOneShellServiceProvider(),
        planner: serviceOrchestrator.getPlannerServiceProvider(),
        plannerGraph: serviceOrchestrator.getPlannerGraphServiceProvider(),
        plp: serviceOrchestrator.getLegacyPlpServiceProvider(),
        projectLandingPage: serviceOrchestrator.getProjectLandingPageServiceProvider(),
        pss: serviceOrchestrator.getProjectSchedulingServiceProvider(),
        publishing: serviceOrchestrator.getPublishingServiceProvider(),
        legacyTcms: serviceOrchestrator.getLegacyTcmsServiceProvider(),
        tcms: serviceOrchestrator.getTcmsServiceProvider(),
        teams: serviceOrchestrator.getTeamsServiceProvider(),
        tenant: serviceOrchestrator.getTenantServiceProvider(),
        plannerTenant: serviceOrchestrator.getPlannerTenantServiceProvider(),
        retail: serviceOrchestrator.getRetailServiceProvider(),
        roaming: serviceOrchestrator.getRoamingServiceProvider(),
        siteURL: serviceOrchestrator.getSiteURLServiceProvider(),
        substrateSearch: serviceOrchestrator.getSubstrateSearchServiceProvider(),
        todo: serviceOrchestrator.getTodoServiceProvider(),
        trouter: serviceOrchestrator.getTrouterServiceProvider(),
        user: serviceOrchestrator.getUserServiceProvider(),
        search: serviceOrchestrator.getSearchServiceProvider(),
        msTeams: serviceOrchestrator.getMsTeamsServiceProvider(),
        modernGroup: serviceOrchestrator.getModernGroupServiceProvider(),
        odsp: serviceOrchestrator.getOdspServiceProvider(),
    };
}
/**
 * Gets the agent providers from the agent factory
 */
export function getAgentProviders(agentFactory) {
    const agentProviders = {
        projectManager: agentFactory.getProjectManagerAgentProvider(),
    };
    return agentProviders;
}
/**
 * Load chunks and initialize deferrable workers
 */
export function loadAndInitializeDeferrableWorkers(workerLoader, loggers, persistentAsyncStorage, configProvider) {
    try {
        const { clientFlavor, clientType } = configProvider().sessionMetaData.appMetadata;
        workerLoader.load(Worker.PerformanceObserver).init();
        workerLoader.load(Worker.DeferredBootOperations).init();
        clientType === ClientType.Web && workerLoader.load(Worker.DocumentTitle).init();
        if (configProvider().flights.EnableUndoRedoInEvoPlans) {
            workerLoader.load(Worker.UndoRedo).init();
        }
        if (clientFlavor !== ClientFlavor.TaskFromMessage) {
            workerLoader.load(Worker.Modal).init();
            workerLoader.load(Worker.Mru).init();
            workerLoader.load(Worker.Navigation).init();
            workerLoader.load(Worker.OperationTracking).init();
            workerLoader.load(Worker.PremiumPlanCreationStatus).init();
            workerLoader.load(Worker.Campaign).init();
            if (!configProvider().changeGates.DisableEvoPlanContextRefresh) {
                workerLoader.load(Worker.PlanContextRefresh).init();
            }
            if (configProvider().flights.EnableUpsellExperienceEvo) {
                workerLoader.load(Worker.ProjectTrialOperationTracker).init();
            }
        }
        if (configProvider().flights.EnablePersistenceEvo) {
            if (persistentAsyncStorage !== null) {
                workerLoader.load(Worker.CacheUpdate).init();
                if (configProvider().flights.EnableCacheEvictionEvo) {
                    workerLoader.load(Worker.CacheEviction).init();
                }
            }
            else {
                loggers.traceLogger.logTrace(0x1e109843 /* tag_4ej7d */, TraceLevel.Info, "Persistent Async Storage is not available. Skipping CacheUpdate worker initialization.");
            }
        }
        if (configProvider().flights.EnableUnoLevelProjectProvisioning &&
            configProvider().settings.EnvironmentFeatureSettings.EnablePremiumPlanExperience) {
            workerLoader.load(Worker.CdsProvisioningStatus).init();
        }
        const shouldInitializeServiceWorkerBootstrap = configProvider().flights.EnableServiceWorkerInEvo &&
            (configProvider().sessionMetaData.appMetadata.clientType === ClientType.Teams ||
                configProvider().sessionMetaData.appMetadata.clientType === ClientType.Web);
        if (shouldInitializeServiceWorkerBootstrap) {
            workerLoader.load(Worker.ServiceWorkerBootstrap).init();
        }
        if (clientFlavor === ClientFlavor.Tab) {
            workerLoader.load(Worker.TeamsTab).init();
        }
        // Initialize Trouter subscription refresh worker when Trouter is enabled
        if (configProvider().flights.EnableTrouterIntegration && !configProvider().settings.trouterSettings.disable) {
            workerLoader.load(Worker.TrouterSubscriptionRefresh).init();
        }
    }
    catch (e) {
        loggers.traceLogger.logTrace(0x1e24179f /* tag_4jb45 */, TraceLevel.Warning, `Failed to load and initialize deferrable workers. [ErrorType=${e.name}][Error=${e.message}]`);
    }
}
/**
 * Load chunks and initialize critical workers
 */
export function loadAndInitializeWorkers(workerLoader, requestPrioritizer, loggers, persistentAsyncStorage, configProvider, hostAdaptor) {
    try {
        const { clientType } = configProvider().sessionMetaData.appMetadata;
        // 1. Initialize the static workers
        // TODO: Add these actions back in once the workers have been ported
        workerLoader.load(Worker.DataIntegrity).init(); // critical because we want to always ensure data integrity
        workerLoader.load(Worker.DataFetch).init(); // critical because we want data fetch to be as fast as possible
        const bootObserverWorker = workerLoader.load(Worker.BootObserver); // critical because we want to make sure we are able to detect exactly when boot is complete
        bootObserverWorker.init();
        if (configProvider().flights.EnablePersistenceEvo) {
            if (persistentAsyncStorage !== null) {
                workerLoader.load(Worker.CacheDataFetch).init(); // critical because we want to load the cached data into the stores
            }
            else {
                loggers.traceLogger.logTrace(0x1e10301a /* tag_4eda0 */, TraceLevel.Info, "Persistent Async Storage is not available. Skipping StoreHydration worker initialization.");
            }
        }
        workerLoader.load(Worker.ChildViewInstantiation).init();
        workerLoader.load(Worker.PlanConversionRedirectWorker).init();
        // Initialize Page Visibility Observer Worker only for Web Client
        clientType === ClientType.Web && workerLoader.load(Worker.PageVisibilityObserver).init();
        // 2. Initialize the workers that are not static but critical be loaded before critical boot
        const networkConnectivityWorker = workerLoader.load(Worker.NetworkConnectivity);
        networkConnectivityWorker.init(); // critical because we want to show some skeleton UI in the absence of a network connection
        networkConnectivityWorker.addNetworkConnectivityChangeCallback(requestPrioritizer.onNetworkConnectivityChanged.bind(requestPrioritizer));
        networkConnectivityWorker.addNetworkConnectivityChangeCallback(setIsOnlineStatusForLogs);
        // 3. Actions to be taken after critical boot is complete
        bootObserverWorker.onBootCompletion(() => {
            // Notify render success on boot completion
            if (clientType === ClientType.Web || (isMetaOSHub(clientType) && configProvider().flights.EnableTeamsNotifySuccessV2)) {
                hostAdaptor.notifyRenderSuccess();
            }
            // Load and initialize the workers that are not critical
            loadAndInitializeDeferrableWorkers(workerLoader, loggers, persistentAsyncStorage, configProvider);
        });
    }
    catch (e) {
        loggers.traceLogger.logTrace(0x1e24179d /* tag_4jb43 */, TraceLevel.Warning, `Failed to load and initialize pre-boot workers. [ErrorType=${e.name}][Error=${e.message}]`);
    }
}
/**
 * Log initial plan view sub route type info for experimentation
 * @param componentName The component name
 * @param subRouteType The current sub route type
 * @param loggers The loggers
 */
export function logPlanViewSubRouteType(componentName, subRouteType, loggers) {
    // If initial route is for a plan view, track in engagement telemetry
    if (subRouteType === SubRouteType.BasicPlan || subRouteType === SubRouteType.PremiumPlan || subRouteType === SubRouteType.TodoList) {
        loggers.engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.OpenPlanFromPersistedRoute, {
            subRouteType: subRouteType,
        });
    }
}
/**
 * Initializes the data pre-fetcher for the application.
 * @param dataPreFetchActionCreator The data pre-fetch action creator
 * @param deepLinkRoute The deep link route descriptor, if any
 * @param context The application context
 * @param hostAdaptor The host adaptor
 * @param loggers The logger context
 * @param configProvider The configuration provider for feature flags and settings
 */
export function initDataPreFetcher(dataPreFetchActionCreator, deepLinkRoute, context, hostAdaptor, loggers, configProvider) {
    const dataPreFetcher = new GenericDataPreFetcher(dataPreFetchActionCreator, deepLinkRoute, context, async (resource) => {
        try {
            loggers.performanceMarker.mark(MarkerTag.GetAccessTokenStart);
            const token = await hostAdaptor.getAccessToken(resource);
            loggers.performanceMarker.mark(MarkerTag.GetAccessTokenComplete);
            return token.accessToken;
        }
        catch (error) {
            if (configProvider().flights.EnableErrorMessageLogging) {
                // The adapter will eventually go into the BaseMsalHelper which logs the exact AuthError
                // We just need to wrap this error into a failed request result
                loggers.traceLogger.logTrace(0x1e1441d7 /* tag_4fehx */, TraceLevel.Warning, `Could not get an auth token: [ErrorType=${error?.name}]`);
                const errorText = "Could not get an access token: AppBootstrap";
                const failureResult = generateAjaxClientError(new Response(errorText, { status: HttpStatus.Unauthorized, statusText: HttpStatusText.Unauthorized }), errorText);
                return Promise.reject(failureResult);
            }
            else {
                // If error message logging is disabled, we throw the error directly
                return Promise.reject(error);
            }
        }
    }, configProvider);
    dataPreFetcher.init();
}
/**
 * Makes a lightweight Graph API call to trigger CAE (Continuous Access Evaluation) enforcement at bootstrap.
 * Workaround: Planner v4 service hasn't implemented CAE, but Graph has. By calling a Graph endpoint
 * (fetchPlanAsync with a dummy plan ID) the service chain's TokenRefreshAndRetryAjaxClient handles any
 * CAE claims challenge automatically. The fetchPlanAsync call is expected to 404 since "TestPlanId" doesn't
 * exist — we only care about triggering the CAE auth flow at the token layer.
 * If it can't resolve the challenge, subsequent real API calls will also fail and the existing MSAL error
 * handler will deal with it.
 *
 * @param configProvider - App configuration provider
 * @param loggers - Logger context
 * @param plannerGraphServiceProvider - Factory for the Graph Planner service (from serviceProviders.plannerGraph)
 */
export async function triggerCaeGraphProbeAsync(configProvider, loggers, plannerGraphServiceProvider) {
    if (configProvider().changeGates.DisableBootstrapCaeProbe) {
        return;
    }
    loggers.performanceMarker.mark(MarkerTag.CaeProbeStart);
    const useFetchPlan = !configProvider().changeGates.DisableBootstrapCaeProbeFetchPlan;
    let probeSucceeded = false;
    try {
        const graphPlannerService = await plannerGraphServiceProvider();
        if (useFetchPlan) {
            await graphPlannerService.fetchPlanAsync({ planId: "TestPlanId" });
        }
        else {
            await graphPlannerService.fetchUserSettingsAsync();
        }
        probeSucceeded = true;
    }
    catch (error) {
        // The TokenRefreshAndRetryAjaxClient already attempted to handle any CAE claims challenge.
        // If it couldn't resolve, the existing MSAL error handler will deal with subsequent failures.
        const responseStatus = error?.response?.status;
        if (useFetchPlan && responseStatus === HttpStatus.NotFound) {
            // Expected: fetchPlanAsync with "TestPlanId" will 404 — the auth flow completed, CAE probe succeeded.
            probeSucceeded = true;
        }
        else {
            loggers.traceLogger.logTrace(0x1e05605a /* tag_4bwb0 */, TraceLevel.Warning, `[Bootstrap] CAE probe failed, continuing.`);
        }
    }
    finally {
        if (probeSucceeded) {
            loggers.traceLogger.logTrace(0x1e05605b /* tag_4bwb1 */, TraceLevel.Info, `[Bootstrap] CAE probe passed.`);
        }
        loggers.performanceMarker.mark(MarkerTag.CaeProbeComplete);
    }
}
